"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"

import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/Components/ui/form"
import { Input } from "@/Components/ui/input"
import { AppContext } from "@/App"

import { useContext } from "react"
import React from "react"
import { useNavigate } from "react-router-dom"
import { format } from "date-fns"
import { dateformat } from "@/utils/GlobalConstants"
import { Charter, CharterContext } from "./Charter"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import TeamBuilder from "../TeamBuilder/team_builder_core/TeamBuilder"
import { PageLockContext } from "../PageLockProvider"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import HelpLink from "../ui/help-link"
import { WizardContext } from "../wizard/wizardProvider"
import Tiptap_editor from "../ui/tiptap/tiptap_editor"
import Tiptap_jsonToHtml from "../ui/tiptap/tiptap_jsonToHtml"
import { useIsMobile } from "@/hooks/use-mobile";
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom";

const formSchema = z.object({
    projectName: z
        .string()
        .min(1, {
            message: "Must be at least 1 character.",
        })
        .max(50, {
            message: "Must not be longer than 50 characters.",
        }),

    //date: z.date(),
    objective: z.string(),
    inScope: z.string(),
    outOfScope: z.string(),
    projectSponsors: z.array(z.object({
        forename: z.string(),
        surname: z.string(),
        userName: z.string(),
    })),
    projectManagers: z.array(z.object({
        forename: z.string(),
        surname: z.string(),
        userName: z.string(),
    })),
    teamMembers: z.array(z.object({
        forename: z.string(),
        surname: z.string(),
        userName: z.string(),
    })),
    criticalSuccessFactors: z.string(),
})

interface CharterPageProps {
    projectList: string[]
}

function Page1_Content({ projectList }: CharterPageProps) {
    const app = useContext(AppContext);
    const isMobile = useIsMobile();
    const zoomLevel = useResponsiveZoom();

    const charterContext = useContext(CharterContext);
    const lock = useContext(PageLockContext);
    const wizard = useContext(WizardContext);

    const charter = charterContext.charterData as Charter;
    const emptyTiptapDocument = '{"type":"doc","content":[{"type":"paragraph"}]}';

    const [allowSubmit, setAllowSubmit] = React.useState(true);

    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogTitle, setDialogTitle] = React.useState("Create New Risk");
    const [dialogTitleBarActions, setDialogTitleBarActions] = React.useState<JSX.Element | null>(null);
    const [dialogContent, setDialogContent] = React.useState<JSX.Element | null>(null);

    // character counters
    const [projectTitleCount, setProjectTitleCount] = React.useState(0);
    // ----------------------------------------------

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            projectName: "",
            //date: new Date(),
            objective: "",
            inScope: "",
            outOfScope: "",
            projectSponsors: [],
            projectManagers: [],
            teamMembers: [],
            criticalSuccessFactors: "",
        },
        mode: "onBlur",
    });

    React.useEffect(() => {

        form.reset({
            projectName: charter.projectName,
            //date: new Date(data.date) ?? new Date(),
            objective: charter.objective ?? "",
            inScope: charter.inScope ?? "",
            outOfScope: charter.outOfScope ?? "",
            projectSponsors: charter.sponsors,
            projectManagers: charter.projectManagers,
            teamMembers: charter.teamMembers,
            criticalSuccessFactors: charter.criticalSuccessFactors ?? "",
        });

        setProjectTitleCount(charter.projectName.length ?? 0);
        checkStepsCompleted();
    }, [charter]);

    React.useEffect(() => {
        charter.projectManagers = app.projectManagers.map(pm => ({
            id: pm.id?.toString() || "", // Ensure id is a string
            forename: pm.forename,
            surname: pm.surname,
            userName: pm.email,
        }));

        charter.sponsors = app.sponsors.map(sponsor => ({
            id: sponsor.id?.toString() || "",
            forename: sponsor.forename,
            surname: sponsor.surname,
            userName: sponsor.email,
        }));

        charter.teamMembers = app.teamMembers.map(member => ({
            id: member.id?.toString() || "",
            forename: member.forename,
            surname: member.surname,
            userName: member.email,
        }));

        form.setValue("projectManagers", charter.projectManagers);
        form.setValue("projectSponsors", charter.sponsors);
        form.setValue("teamMembers", charter.teamMembers);
        checkStepsCompleted();
    }, [app.projectManagers, app.sponsors, app.teamMembers])

    React.useEffect(() => {
        if (projectList.includes(form.watch("projectName").trim())) {
            form.setError("projectName", {
                type: "manual",
                message: "Project name is already in use"
            });
            setAllowSubmit(false);
        } else {
            form.clearErrors("projectName");
            setAllowSubmit(true);
        }
    }, [form.watch("projectName")]);

    React.useEffect(() => {
        if (!lock.viewOnly && charterContext.charterLocked) {
            showCharterLockedWarning();
        }
    }, [lock.viewOnly, charterContext.charterLocked]);

    function showTeamBuilderDialog(userType: "Viewer" | "Team Member" | "Sponsor" | "Project Manager" | undefined) {
        setDialogTitle("Select Your");
        setDialogTitleBarActions(<DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button className="underline ml-2">{userType ? userType + 's' : "Whole Team"}</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => showTeamBuilderDialog("Sponsor")}>Sponsors</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => showTeamBuilderDialog("Project Manager")}>Project Managers</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => showTeamBuilderDialog("Team Member")}>Team Members</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => showTeamBuilderDialog(undefined)}>Whole Team</DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>);
        setDialogContent(<TeamBuilder key={userType} typeFilter={userType} />);
        setDialogOpen(true);
    }

    function showCharterLockedWarning() {
        setDialogTitle("Charter Locked");
        setDialogContent(<p className={`${isMobile ? "text-xl" : ""}`}>The charter has been approved and cannot be edited.<br /> If you wish to make changes, please create a Project Change Request.</p>);
        setDialogOpen(true);
    }

    function validateProjectName() {
        if (projectList.includes(form.watch("projectName").trim())) {
            form.setError("projectName", {
                type: "manual",
                message: "Project name is already in use"
            });
            setAllowSubmit(false);
        } else {
            form.clearErrors("projectName");
            setAllowSubmit(true);
            submitChanges();
        }
    }

    function submitChanges() {
        if (!allowSubmit) {
            return;
        }
        // make cursor busy
        document.body.style.cursor = 'wait';
        fetch(`Projects/charter/${app.selectedProject?.id}/`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...form.getValues(),
                projectId: app.selectedProject?.id,
                sponsorApprovalDate: charter.sponsorApprovalDate, // include original approval date as indicator that it has been approved before, and leave the project changes tab visible
            }),
        })
            .then((res) => {
                if (res.status === 204) {// No content to parse
                    // Update the project name in the app context
                    if (app.selectedProject?.name !== form.getValues().projectName) {
                        app.setProjects((prevList) =>
                            prevList.map((project) =>
                                project.id === app.selectedProject?.id
                                    ? { ...project, name: form.getValues().projectName }
                                    : project
                            )
                        );
                    }
                    charterContext.setCharterData({
                        ...charter,
                        projectName: form.getValues().projectName,
                        objective: form.getValues().objective,
                        inScope: form.getValues().inScope,
                        outOfScope: form.getValues().outOfScope,
                        criticalSuccessFactors: form.getValues().criticalSuccessFactors,
                        sponsorApproval: false,
                        submittedForApproval: false, // Reset submitted for approval status
                        sponsorApprovalDate: null, // Reset approval date
                        submissionDate: null, // Reset submission date
                        submissionSeen: false, // Reset submission seen status
                    });
                    checkStepsCompleted();
                    app.showActionNotification("Saved", "Project Charter Updated", "success");
                    // release cursor
                    document.body.style.cursor = 'default';
                } else {
                    app.showActionNotification("Error", "Failed to save changes to the server", "destructive");
                }
            })
    }

    function checkStepsCompleted() {
        wizard?.wizardSteps.forEach(step => {
            switch (step.stepComponentId) {
                case "projectName":
                    wizard?.updateWizardStepByElementId(step.stepComponentId, charter.projectName !== "" && charter.projectName !== step.defaultValue);
                    return;
                case "objectives":
                    if (charter.objective === null || charter.objective === undefined || charter.objective === emptyTiptapDocument) {
                        wizard?.updateWizardStepByElementId(step.stepComponentId, false);
                        return;
                    }
                    wizard?.updateWizardStepByElementId(step.stepComponentId, charter.objective.length > 0);
                    return;
                case "inScope":
                    if (charter.inScope === null || charter.inScope === undefined || charter.inScope === emptyTiptapDocument) {
                        wizard?.updateWizardStepByElementId(step.stepComponentId, false);
                        return;
                    }
                    wizard?.updateWizardStepByElementId(step.stepComponentId, charter.inScope.length > 0);
                    return;
                case "outOfScope":
                    if (charter.outOfScope === null || charter.outOfScope === undefined || charter.outOfScope === emptyTiptapDocument) {
                        wizard?.updateWizardStepByElementId(step.stepComponentId, false);
                        return;
                    }
                    wizard?.updateWizardStepByElementId(step.stepComponentId, charter.outOfScope.length > 0);
                    return;
                case "projectSponsors":
                    if (charter.sponsors === null || charter.sponsors === undefined) {
                        wizard?.updateWizardStepByElementId(step.stepComponentId, false);
                        return;
                    }
                    wizard?.updateWizardStepByElementId(step.stepComponentId, charter.sponsors.length > 0);
                    return;
                case "projectManagers":
                    if (charter.projectManagers === null || charter.projectManagers === undefined) {
                        wizard?.updateWizardStepByElementId(step.stepComponentId, false);
                        return;
                    }
                    wizard?.updateWizardStepByElementId(step.stepComponentId, charter.projectManagers.length > 0);
                    return;
                case "projectTeam":
                    if (charter.teamMembers === null || charter.teamMembers === undefined) {
                        wizard?.updateWizardStepByElementId(step.stepComponentId, false);
                        return;
                    }
                    wizard?.updateWizardStepByElementId(step.stepComponentId, charter.teamMembers.length > 0);
                    return;
                case "criticalSuccessFactors":
                    if (charter.criticalSuccessFactors === null || charter.criticalSuccessFactors === undefined || charter.criticalSuccessFactors === emptyTiptapDocument) {
                        wizard?.updateWizardStepByElementId(step.stepComponentId, false);
                        return;
                    }
                    wizard?.updateWizardStepByElementId(step.stepComponentId, charter.criticalSuccessFactors.length > 0);
                    return;
                case "highLevelTimeline":
                    if (charter.majorMilestones === null || charter.majorMilestones === undefined) {
                        wizard?.updateWizardStepByElementId(step.stepComponentId, false);
                        return;
                    }
                    wizard?.updateWizardStepByElementId(step.stepComponentId, charter.majorMilestones.length > 0);
                    return;
                default:
                    return;
            }
        });
    }

    if (isMobile) {
        return (
            <div className="flex flex-col pt-2 mb-4">
                {wizard && wizard.getWizardPointer()}
                {wizard.getProgressMeter()}
                <div>
                    <Form {...form} >
                        <div className="flex flex-col items-center mb-4">
                            <div className="flex flex-row w-full mb-4">
                                <div className="flex bg-table-header rounded-md w-full">
                                    <div className="self-center text-xl text-table-header-foreground px-2">
                                        Status
                                    </div>
                                    <div className="rounded-none bg-table-body   p-2 w-full text-xl">
                                        {charter.sponsorApproval ? "Approved" : charter.submittedForApproval ? "Submitted for Approval" : "Unapproved"}
                                    </div>
                                </div>
                                <div className="flex bg-table-header w-1/2 rounded-r-md">
                                    <div className="self-center text-xl px-2">
                                        Date
                                    </div>
                                    <div className="rounded-none bg-table-body   p-2 w-full text-xl rounded-r-md">
                                        {charter.sponsorApprovalDate ? format(new Date(charter.sponsorApprovalDate), dateformat) : "N/A"}
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-row w-full">
                                <div className="flex bg-table-header rounded-md w-full">
                                    <FormField
                                        control={form.control}
                                        name="projectName"
                                        render={({ field }) => (
                                            <FormItem id="projectName" className="flex flex-row w-full space-y-0">
                                                <FormLabel className="self-center m-2 text-xl">Project: </FormLabel>
                                                {(app.authLevel < 3 || charterContext.charterLocked || lock.viewOnly) ? (
                                                    <div className="rounded-r-md p-2 w-full text-xl h-12 border-gray-200 bg-table-body mb-2 mt-0  ">
                                                        {field.value}
                                                    </div>
                                                ) : (
                                                    <>
                                                        <FormControl>
                                                            <Input className="rounded-none bg-table-body text-xl h-12 mb-2 mt-0 grow" placeholder="" {...field}
                                                                onBlur={
                                                                    form.handleSubmit(validateProjectName)
                                                                }
                                                                onChange={(e) => {
                                                                    setProjectTitleCount(e.currentTarget.value.length)
                                                                    field.onChange(e);
                                                                }}
                                                                maxLength={50}
                                                                tabIndex={1}
                                                            />

                                                        </FormControl>
                                                        <span className="text-lg flex text-gray-50 items-end pl-4 pr-2">{projectTitleCount}/50</span>
                                                        <FormMessage className="p-2 ml-4 bg-gray-400 rounded-md text-nowrap" />
                                                    </>
                                                )}
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="absolute top-0 right-2 -translate-y-[125%]"><div className="flex flex-row ml-auto w-min text-nowrap">{lock.getRefreshControl()}{(app.authLevel == 4 || app.authLevel == 5) && lock.getLockControl()}</div></div>
                        </div>
                        <div className="flex flex-col">
                            <FormField
                                control={form.control}
                                name="objective"
                                render={({ field }) => (
                                    <FormItem id="objectives" className="space-y-0">
                                        <div className="flex flex-row justify-between bg-table-header rounded-t-md">
                                            <FormLabel className="self-center m-2 text-xl text-table-header-foreground">Objective</FormLabel>
                                        </div>
                                        {(app.authLevel < 3 || charterContext.charterLocked || lock.viewOnly) ? (
                                            <div className="rounded-b-md p-2 text-xl border-gray-200 bg-table-body mb-2 mt-0">
                                                {(field.value == emptyTiptapDocument || field.value === null || field.value === undefined || field.value === '') ? <span className="text-gray-400">No objective set</span> : <Tiptap_jsonToHtml json={field.value} />}
                                            </div>
                                        ) : (
                                            <>
                                                <FormControl>
                                                    <Tiptap_editor key={field.value} content={field.value}
                                                        onBlur={(value) => { field.onChange(value); form.handleSubmit(submitChanges)(); }}
                                                        placeholder={
                                                            [
                                                                "Example:",
                                                                "- Increase manufacturing output of the assembly line ABC from 200 to 400 units per shift by 31-Jan-2027",
                                                            ].join("\n")
                                                        }
                                                        minHeight="100px"
                                                        tabIndex={2}
                                                    />
                                                </FormControl>
                                                <FormMessage className="p-2 bg-gray-500 rounded-md text-nowrap" />
                                            </>
                                        )}
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="inScope"
                                render={({ field }) => (
                                    <FormItem id="inScope" className="mt-2 space-y-0">
                                        <div className="flex flex-row justify-between bg-table-header rounded-t-md">
                                            <FormLabel className="self-center m-2 text-xl">In Scope</FormLabel>
                                        </div>
                                        {(app.authLevel < 3 || charterContext.charterLocked || lock.viewOnly) ? (
                                            <div className="rounded-b-md p-2 text-xl border-gray-200 bg-table-body mb-2 mt-0">
                                                {(field.value == emptyTiptapDocument || field.value === null || field.value === undefined || field.value === '') ? <span className="text-gray-400">No in scope details set</span> : <Tiptap_jsonToHtml json={field.value} />}
                                            </div>
                                        ) : (
                                            <>
                                                <FormControl>
                                                    <Tiptap_editor key={field.value} content={field.value}
                                                        onBlur={(value) => { field.onChange(value); form.handleSubmit(submitChanges)(); }}
                                                        placeholder={
                                                            [
                                                                "Example:",
                                                                "- Manufacturing assembly line ABC for the following products:​",
                                                                "   - Product Family Ziggy 3D​",
                                                                "   - Product Family Marley 4D​",
                                                                "- The processes included in scope are as follows:​",
                                                                "   - Welding station on assembly line ABC​",
                                                                "   - Soldering station on assembly line ABC​",
                                                                "   - End of line inspection on assembly line ABC",
                                                            ].join("\n")
                                                        }
                                                        minHeight={isMobile ? "225px" : "200px"}
                                                        tabIndex={3}
                                                    />
                                                </FormControl>
                                                <FormMessage className="p-2 bg-gray-400 rounded-md text-nowrap" />
                                            </>
                                        )}
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="outOfScope"
                                render={({ field }) => (
                                    <FormItem id="outOfScope" className="mt-2 space-y-0">
                                        <div className="flex flex-row justify-between bg-table-header rounded-t-md">
                                            <FormLabel className="self-center m-2 text-xl">Out of Scope</FormLabel>
                                        </div>
                                        {(app.authLevel < 3 || charterContext.charterLocked || lock.viewOnly) ? (
                                            <div className="rounded-b-md p-2 text-xl border-gray-200 bg-table-body mb-2 mt-0  ">
                                                {(field.value == emptyTiptapDocument || field.value === null || field.value === undefined || field.value === '') ? <span className="text-gray-400">No out of scope details set</span> : <Tiptap_jsonToHtml json={field.value} />}
                                            </div>
                                        ) : (
                                            <>
                                                <FormControl>
                                                    <Tiptap_editor key={field.value} content={field.value}
                                                        onBlur={(value) => { field.onChange(value); form.handleSubmit(submitChanges)(); }}
                                                        placeholder={
                                                            [
                                                                "Example:",
                                                                "- Any other assembly lines outside of assembly line ABC",
                                                                "- Any other product families apart from:​",
                                                                "   - Product Family Ziggy 3D​",
                                                                "   - Product Family Marley 4D​",
                                                                "- Any other processes apart from:​",
                                                                "   - Welding station on assembly line ABC​",
                                                                "   - Soldering station on assembly line ABC​",
                                                                "   - End of line inspection on assembly line ABC",
                                                            ].join("\n")
                                                        }
                                                        minHeight={isMobile ? "250px" : "200px"}
                                                        tabIndex={4}
                                                    />
                                                </FormControl>
                                                <FormMessage className="p-2 bg-gray-400 rounded-md text-nowrap" />
                                            </>
                                        )}
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="projectSponsors"
                                render={({ field }) => (
                                    <FormItem id="projectSponsors" className="mt-2 bg-table-header rounded-md">
                                        <FormLabel className="self-center m-2 text-xl">Project Sponsors</FormLabel>
                                        <div className={"flex flex-col rounded-b-md p-2 text-xl border-gray-200 bg-table-body mb-2 mt-0  "
                                            + (app.authLevel > 3 ? " hover:cursor-pointer" : "")
                                        }
                                            onClick={() => { if (app.authLevel > 3) { showTeamBuilderDialog("Sponsor") } }} tabIndex={5}>
                                            {field.value.length === 0 ? (
                                                <div className="text-gray-400">Click here to add your projects sponsors</div>
                                            ) : (
                                                field.value.map((sponsor, index) => (
                                                    <div key={index} className="flex flex-row justify-between">
                                                        <div>{sponsor.forename} {sponsor.surname}</div>
                                                        <div>{sponsor.userName}</div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="projectManagers"
                                render={({ field }) => (
                                    <FormItem id="projectManagers" className="mt-4 justify-between bg-table-header rounded-md">
                                        <FormLabel className="self-center m-2 text-xl">Project Management</FormLabel>
                                        <div className={"flex flex-col rounded-b-md p-2 text-xl border-gray-200 bg-table-body mb-2 mt-0  "
                                            + (app.authLevel > 3 ? " hover:cursor-pointer" : "")
                                        }
                                            onClick={() => { if (app.authLevel > 3) { showTeamBuilderDialog("Project Manager") } }} tabIndex={6}>
                                            {field.value.length === 0 ? (
                                                <div className="text-gray-400 text-xl">Click here to add your project managers</div>
                                            ) : (
                                                field.value.map((managers, index) => (
                                                    <div key={index} className="flex flex-row justify-between text-xl">
                                                        <div>{managers.forename} {managers.surname}</div><div>{managers.userName}</div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="teamMembers"
                                render={({ field }) => (
                                    <FormItem id="projectTeam" className="mt-4 justify-between bg-table-header rounded-md">
                                        <FormLabel className="self-center m-2 text-xl">Project Team</FormLabel>
                                        <div className={"flex flex-col rounded-b-md p-2 text-xl border-gray-200 bg-table-body mb-2 mt-0  "
                                            + (app.authLevel > 3 ? " hover:cursor-pointer" : "")
                                        }
                                            onClick={() => { if (app.authLevel > 3) { showTeamBuilderDialog("Team Member") } }} tabIndex={7}>
                                            {field.value.length === 0 ? (
                                                <div className="text-gray-400 text-xl">Click here to add your team members</div>
                                            ) : (
                                                field.value.map((team, index) => (
                                                    <div key={index} className="flex flex-row justify-between text-xl">
                                                        <div>{team.forename} {team.surname}</div><div className="truncate">{team.userName}</div>
                                                    </div>))
                                            )
                                            }
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                disabled={app.authLevel < 3 || charterContext.charterLocked || lock.viewOnly}
                                control={form.control}
                                name="criticalSuccessFactors"
                                render={({ field }) => (
                                    <FormItem id="criticalSuccessFactors" className="mt-4 space-y-0">
                                        <div className="flex flex-row justify-between bg-table-header rounded-t-md">
                                            <FormLabel className="self-center m-2 text-xl">Critical Success Factors</FormLabel>
                                        </div>
                                        {(app.authLevel < 3 || charterContext.charterLocked || lock.viewOnly) ? (
                                            <div className="rounded-b-md p-2 text-xl border-gray-200 bg-table-body mb-2 mt-0  ">
                                                {(field.value === emptyTiptapDocument || field.value === null || field.value === undefined || field.value === '') ? <span className="text-gray-400">No critical success factors set</span> : <Tiptap_jsonToHtml json={field.value} />}
                                            </div>
                                        ) : (
                                            <>
                                                <FormControl>
                                                    <Tiptap_editor key={field.value} content={field.value}
                                                        onBlur={(value) => { field.onChange(value); form.handleSubmit(submitChanges)(); }}
                                                        placeholder={
                                                            [
                                                                "Example:",
                                                                "- Process Specification for Ziggy 3D Welding Soldering & End of line Inspection",
                                                                "- Process Specification Marley 4D Welding Soldering & End of line Inspection",
                                                                "- Process validation protocols Ziggy 3D Welding Soldering & End of line Inspection",
                                                                "- Process validation protocols Marley 4D Welding Soldering & End of line Inspection",
                                                                "- Availability of 6 hours of engineering time on the ABC assembly line",
                                                                "- End of line inspection Protocol for Ziggy 3D",
                                                                "- End of line inspection Protocol for Marley 4D",
                                                                "- Availability of the following operators",
                                                                "   - Operator #1",
                                                                "   - Operator #2",
                                                                "   - Available for 6 hours on the DD-MMM-YY",
                                                            ].join("\n")
                                                        }
                                                        minHeight="320px"
                                                        tabIndex={8}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </>
                                        )}
                                    </FormItem>
                                )}
                            />
                        </div>
                    </Form>

                    <table id="highLevelTimeline" className={"w-full mt-4 table-auto text-table-text text-xl bg-table-body rounded-md" + (app.authLevel > 1 ? " hover:cursor-pointer" : "")} onClick={() => { if (app.authLevel > 1) { navigate("/Plan") } }}>
                        <thead className="bg-table-header rounded-t-md text-table-header-foreground">
                            <tr>
                                <th className="p-1 rounded-tl-md">High Level Timeline</th>
                                <th className="text-center w-1/5 p-1 rounded-tr-md">Finish Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {charter.majorMilestones.length > 0 ?
                                charter.majorMilestones.map((wp, wpIndex) => (
                                    <React.Fragment key={wpIndex}>
                                        <tr className="bg-table-subheader text-table-subheader-foreground">
                                            <td className="pl-1">{wp.workPackageName}</td>
                                            <td></td>
                                        </tr>
                                        {[...wp.tasks]
                                            .sort((a, b) => new Date(a.finishDate).getTime() - new Date(b.finishDate).getTime())
                                            .map((task, taskIndex) => (
                                                <tr key={taskIndex}>
                                                    <td className="pl-2">{task.name}</td>
                                                    <td className="text-center">{format(task.finishDate, dateformat)}</td>
                                                </tr>
                                            ))}
                                    </React.Fragment>
                                ))
                                :
                                <tr>
                                    <td>No Data to Show</td><td></td>
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="flex flex-col w-[95%]" style={{ zoom: zoomLevel }}>
                        <DialogHeader>
                            <DialogTitle><div className="flex text-3xl"><div className="my-auto">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                        </DialogHeader>
                        {dialogContent}
                    </DialogContent>
                </Dialog>
            </div >
        )
    }

    return (
        <div className="flex flex-col pt-2 relative">
            {wizard && wizard.getWizardPointer()}
            <Form {...form} >
                <div className="flex flex-row justify-between items-center mx-4">
                    <div className="flex flex-row w-1/3">
                        <div className="flex bg-table-header rounded-md w-full mr-5">
                            <FormField
                                control={form.control}
                                name="projectName"
                                render={({ field }) => (
                                    <FormItem id="projectName" className="flex flex-row w-full space-y-0">
                                        <FormLabel className="self-center m-2 text-table-header-foreground">Project: </FormLabel>
                                        {(app.authLevel < 3 || charterContext.charterLocked || lock.viewOnly) ? (
                                            <div className="rounded-r-md p-2 w-full md:text-sm border-gray-200 bg-table-body mb-2 mt-0  ">
                                                {field.value}
                                            </div>
                                        ) : (
                                            <>
                                                <FormControl>

                                                    <Input className="rounded-none bg-table-body mb-2 mt-0 grow" placeholder="" {...field}
                                                        onBlur={
                                                            form.handleSubmit(validateProjectName)
                                                        }
                                                        onChange={(e) => {
                                                            setProjectTitleCount(e.currentTarget.value.length)
                                                            field.onChange(e);
                                                        }}
                                                        maxLength={50}
                                                        tabIndex={1}
                                                    />

                                                </FormControl>
                                                <span className="text-xs flex text-gray-50 items-end pl-4 pr-2">{projectTitleCount}/50</span>
                                                <FormMessage className="p-2 ml-4 bg-gray-400 rounded-md text-nowrap" />
                                            </>
                                        )}
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className="flex flex-row w-1/3 px-1">
                        <div className="flex bg-table-header rounded-md w-1/2">
                            <div className="self-center text-sm text-table-header-foreground px-2">
                                Status
                            </div>
                            <div className="rounded-none bg-table-body   p-2 w-full text-sm">
                                {charter.sponsorApproval ? "Approved" : charter.submittedForApproval ? "Submitted for Approval" : "Unapproved"}
                            </div>
                        </div>
                        <div className="flex bg-table-header w-1/2 rounded-r-md">
                            <div className="self-center text-sm text-table-header-foreground px-2">
                                Date
                            </div>
                            <div className="rounded-none bg-table-body   p-2 w-full text-sm rounded-r-md">
                                {charter.sponsorApprovalDate ? format(new Date(charter.sponsorApprovalDate), dateformat) : "N/A"}
                            </div>
                        </div>
                    </div>
                    <div className="w-1/3"><div className="flex flex-row ml-auto w-min text-nowrap">{lock.getRefreshControl()}{(app.authLevel == 4 || app.authLevel == 5) && lock.getLockControl()}<HelpLink page="charter" /></div></div>
                </div>
                <div className="flex flex-row">
                    <div className="flex flex-col w-1/3 p-2 m-2">
                        <FormField
                            control={form.control}
                            name="objective"
                            render={({ field }) => (
                                <FormItem id="objectives" className="space-y-0">
                                    <div className="flex flex-row justify-between bg-table-header rounded-t-md">
                                        <FormLabel className="self-center m-2 text-table-header-foreground">Objective</FormLabel>
                                    </div>
                                    {(app.authLevel < 3 || charterContext.charterLocked || lock.viewOnly) ? (
                                        <div className="rounded-b-md p-2 md:text-sm border-gray-200 bg-table-body mb-2 mt-0  ">
                                            {(field.value == emptyTiptapDocument || field.value === null || field.value === undefined || field.value === '') ? <span className="text-gray-400">No objective set</span> : <Tiptap_jsonToHtml json={field.value} />}
                                        </div>
                                    ) : (
                                        <>
                                            <FormControl>
                                                <Tiptap_editor key={field.value} content={field.value}
                                                    onBlur={(value) => { field.onChange(value); form.handleSubmit(submitChanges)(); }}
                                                    placeholder={
                                                        [
                                                            "Example:",
                                                            "- Increase manufacturing output of the assembly line ABC from 200 to 400 units per shift by 31-Jan-2027",
                                                        ].join("\n")
                                                    }
                                                    minHeight="100px"
                                                    tabIndex={2}
                                                />
                                            </FormControl>
                                            <FormMessage className="p-2 bg-gray-500 rounded-md text-nowrap" />
                                        </>
                                    )}
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="inScope"
                            render={({ field }) => (
                                <FormItem id="inScope" className="mt-4 space-y-0">
                                    <div className="flex flex-row justify-between bg-table-header rounded-t-md">
                                        <FormLabel className="self-center m-2 text-table-header-foreground">In Scope</FormLabel>
                                    </div>
                                    {(app.authLevel < 3 || charterContext.charterLocked || lock.viewOnly) ? (
                                        <div className="rounded-b-md p-2 md:text-sm border-gray-200 bg-table-body mb-2 mt-0  ">
                                            {(field.value == emptyTiptapDocument || field.value === null || field.value === undefined || field.value === '') ? <span className="text-gray-400">No in scope details set</span> : <Tiptap_jsonToHtml json={field.value} />}
                                        </div>
                                    ) : (
                                        <>
                                            <FormControl>
                                                <Tiptap_editor key={field.value} content={field.value}
                                                    onBlur={(value) => { field.onChange(value); form.handleSubmit(submitChanges)(); }}
                                                    placeholder={
                                                        [
                                                            "Example:",
                                                            "- Manufacturing assembly line ABC for the following products:​",
                                                            "   - Product Family Ziggy 3D​",
                                                            "   - Product Family Marley 4D​",
                                                            "- The processes included in scope are as follows:​",
                                                            "   - Welding station on assembly line ABC​",
                                                            "   - Soldering station on assembly line ABC​",
                                                            "   - End of line inspection on assembly line ABC",
                                                        ].join("\n")
                                                    }
                                                    minHeight="200px"
                                                    tabIndex={3}
                                                />
                                            </FormControl>
                                            <FormMessage className="p-2 bg-gray-400 rounded-md text-nowrap" />
                                        </>
                                    )}
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="outOfScope"
                            render={({ field }) => (
                                <FormItem id="outOfScope" className="mt-4 space-y-0">
                                    <div className="flex flex-row justify-between bg-table-header rounded-t-md">
                                        <FormLabel className="self-center m-2 text-table-header-foreground">Out of Scope</FormLabel>
                                    </div>
                                    {(app.authLevel < 3 || charterContext.charterLocked || lock.viewOnly) ? (
                                        <div className="rounded-b-md p-2 md:text-sm border-gray-200 bg-table-body mb-2 mt-0  ">
                                            {(field.value == emptyTiptapDocument || field.value === null || field.value === undefined || field.value === '') ? <span className="text-gray-400">No out of scope details set</span> : <Tiptap_jsonToHtml json={field.value} />}
                                        </div>
                                    ) : (
                                        <>
                                            <FormControl>
                                                <Tiptap_editor key={field.value} content={field.value}
                                                    onBlur={(value) => { field.onChange(value); form.handleSubmit(submitChanges)(); }}
                                                    placeholder={
                                                        [
                                                            "Example:",
                                                            "- Any other assembly lines outside of assembly line ABC",
                                                            "- Any other product families apart from:​",
                                                            "   - Product Family Ziggy 3D​",
                                                            "   - Product Family Marley 4D​",
                                                            "- Any other processes apart from:​",
                                                            "   - Welding station on assembly line ABC​",
                                                            "   - Soldering station on assembly line ABC​",
                                                            "   - End of line inspection on assembly line ABC",
                                                        ].join("\n")
                                                    }
                                                    minHeight="200px"
                                                    tabIndex={4}
                                                />
                                            </FormControl>
                                            <FormMessage className="p-2 bg-gray-400 rounded-md text-nowrap" />
                                        </>
                                    )}
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-col w-1/3 mt-4 mx-2">
                        <table id="highLevelTimeline" className={"table-auto text-table-text bg-table-body rounded-md" + (app.authLevel > 1 ? " hover:cursor-pointer" : "")} onClick={() => { if (app.authLevel > 1) { navigate("/Plan") } }}>
                            <thead className="bg-table-header rounded-t-md text-table-header-foreground">
                                <tr>
                                    <th className="p-1 rounded-tl-md">High Level Timeline</th>
                                    <th className="text-center w-1/5 p-1 rounded-tr-md">Finish Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {charter.majorMilestones.length > 0 ?
                                    charter.majorMilestones.map((wp, wpIndex) => (
                                        <React.Fragment key={wpIndex}>
                                            <tr className="bg-table-subheader text-table-subheader-foreground">
                                                <td className="pl-1">{wp.workPackageName}</td>
                                                <td></td>
                                            </tr>
                                            {[...wp.tasks]
                                                .sort((a, b) => new Date(a.finishDate).getTime() - new Date(b.finishDate).getTime())
                                                .map((task, taskIndex) => (
                                                    <tr key={taskIndex}>
                                                        <td className="pl-2">{task.name}</td>
                                                        <td className="text-center">{format(task.finishDate, dateformat)}</td>
                                                    </tr>
                                                ))}
                                        </React.Fragment>
                                    ))
                                    :
                                    <tr>
                                        <td>No Data to Show</td><td></td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                    <div className="flex flex-col w-1/3 p-2 m-2">

                        <FormField
                            control={form.control}
                            name="projectSponsors"
                            render={({ field }) => (
                                <FormItem id="projectSponsors" className="justify-between bg-table-header rounded-md">
                                    <FormLabel className="self-center m-2 text-table-header-foreground">Project Sponsors</FormLabel>
                                    <div className={"flex flex-col rounded-b-md p-2 md:text-sm border-gray-200 bg-table-body mb-2 mt-0  "
                                        + (app.authLevel > 3 ? " hover:cursor-pointer" : "")
                                    }
                                        onClick={() => { if (app.authLevel > 3) { showTeamBuilderDialog("Sponsor") } }} tabIndex={5}>
                                        {field.value.length === 0 ? (
                                            <div className="text-gray-400">Click here to add your projects sponsors</div>
                                        ) : (
                                            field.value.map((sponsor, index) => (
                                                <div key={index} className="flex flex-row justify-between">
                                                    <div>{sponsor.forename} {sponsor.surname}</div>
                                                    <div>{sponsor.userName}</div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="projectManagers"
                            render={({ field }) => (
                                <FormItem id="projectManagers" className="mt-4 justify-between bg-table-header rounded-md">
                                    <FormLabel className="self-center m-2 text-table-header-foreground">Project Management</FormLabel>
                                    <div className={"flex flex-col rounded-b-md p-2 md:text-sm border-gray-200 bg-table-body mb-2 mt-0  "
                                        + (app.authLevel > 3 ? " hover:cursor-pointer" : "")
                                    }
                                        onClick={() => { if (app.authLevel > 3) { showTeamBuilderDialog("Project Manager") } }} tabIndex={6}>
                                        {field.value.length === 0 ? (
                                            <div className="text-gray-400">Click here to add your project managers</div>
                                        ) : (
                                            field.value.map((managers, index) => (
                                                <div key={index} className="flex flex-row justify-between">
                                                    <div>{managers.forename} {managers.surname}</div><div>{managers.userName}</div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="teamMembers"
                            render={({ field }) => (
                                <FormItem id="projectTeam" className="mt-4 justify-between bg-table-header rounded-md">
                                    <FormLabel className="self-center m-2 text-table-header-foreground">Project Team</FormLabel>
                                    <div className={"flex flex-col rounded-b-md p-2 md:text-sm border-gray-200 bg-table-body mb-2 mt-0  "
                                        + (app.authLevel > 3 ? " hover:cursor-pointer" : "")
                                    }
                                        onClick={() => { if (app.authLevel > 3) { showTeamBuilderDialog("Team Member") } }} tabIndex={7}>
                                        {field.value.length === 0 ? (
                                            <div className="text-gray-400">Click here to add your team members</div>
                                        ) : (
                                            field.value.map((team, index) => (
                                                <div key={index} className="flex flex-row justify-between">
                                                    <div>{team.forename} {team.surname}</div><div className="truncate">{team.userName}</div>
                                                </div>))
                                        )
                                        }
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />


                        <FormField
                            disabled={app.authLevel < 3 || charterContext.charterLocked || lock.viewOnly}
                            control={form.control}
                            name="criticalSuccessFactors"
                            render={({ field }) => (
                                <FormItem id="criticalSuccessFactors" className="mt-4 space-y-0">
                                    <div className="flex flex-row justify-between bg-table-header rounded-t-md">
                                        <FormLabel className="self-center m-2 text-table-header-foreground">Critical Success Factors</FormLabel>
                                    </div>
                                    {(app.authLevel < 3 || charterContext.charterLocked || lock.viewOnly) ? (
                                        <div className="rounded-b-md p-2 md:text-sm border-gray-200 bg-table-body mb-2 mt-0  ">
                                            {(field.value === emptyTiptapDocument || field.value === null || field.value === undefined || field.value === '') ? <span className="text-gray-400">No critical success factors set</span> : <Tiptap_jsonToHtml json={field.value} />}
                                        </div>
                                    ) : (
                                        <>
                                            <FormControl>
                                                <Tiptap_editor key={field.value} content={field.value}
                                                    onBlur={(value) => { field.onChange(value); form.handleSubmit(submitChanges)(); }}
                                                    placeholder={
                                                        [
                                                            "Example:",
                                                            "- Process Specification for Ziggy 3D Welding Soldering & End of line Inspection",
                                                            "- Process Specification Marley 4D Welding Soldering & End of line Inspection",
                                                            "- Process validation protocols Ziggy 3D Welding Soldering & End of line Inspection",
                                                            "- Process validation protocols Marley 4D Welding Soldering & End of line Inspection",
                                                            "- Availability of 6 hours of engineering time on the ABC assembly line",
                                                            "- End of line inspection Protocol for Ziggy 3D",
                                                            "- End of line inspection Protocol for Marley 4D",
                                                            "- Availability of the following operators",
                                                            "   - Operator #1",
                                                            "   - Operator #2",
                                                            "   - Available for 6 hours on the DD-MMM-YY",
                                                        ].join("\n")
                                                    }
                                                    minHeight="320px"
                                                    tabIndex={8}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </>
                                    )}
                                </FormItem>
                            )}
                        />
                        {wizard.getProgressMeter()}
                    </div>
                </div>
            </Form>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="flex flex-col min-w-fit max-w-[1600px]" style={{ zoom: zoomLevel }}>
                    <DialogHeader>
                        <DialogTitle><div className="flex"><div className="my-auto">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                    </DialogHeader>
                    {dialogContent}
                </DialogContent>
            </Dialog>
        </div >
    )
}

export default Page1_Content